import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { UploadCloud, Wallet, IndianRupee, ReceiptText, Download } from "lucide-react";

const statusColor = (s) => ({
  Pending: "badge-yellow", Successful: "badge-green", Failed: "badge-red",
}[s] || "badge-gray");

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [note, setNote] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const r = await api.get("/wallet");
    setWallet(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setMsg("");
    try {
      const fd = new FormData();
      fd.append("amount", amount);
      fd.append("utr", utr);
      fd.append("note", note);
      if (screenshot) fd.append("screenshot", screenshot);
      await api.post("/payments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Payment submitted for verification");
      setAmount(""); setUtr(""); setNote(""); setScreenshot(null);
      await load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Failed to submit payment");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = async (p) => {
    const token = localStorage.getItem("crm_token");
    const res = await fetch(`${API_BASE}/payments/${p.id}/receipt`, {
      headers: { Authorization: `Bearer ${token}` }, credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${p.payment_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!wallet) return <div>Loading…</div>;

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="wallet-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold flex items-center gap-2"><Wallet size={28} /> Wallet & Payments</h1>
        <p className="text-sm text-muted-foreground">Scan the QR to pay via UPI, then submit the UTR / reference for verification.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Paid (Approved)</div>
          <div className="font-heading text-3xl font-extrabold mt-2 tabular flex items-center gap-1"><IndianRupee size={22} />{wallet.total_paid.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Pending Verification</div>
          <div className="font-heading text-3xl font-extrabold mt-2 tabular flex items-center gap-1"><IndianRupee size={22} />{wallet.pending.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Transactions</div>
          <div className="font-heading text-3xl font-extrabold mt-2 tabular">{wallet.payments.length}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-6" data-testid="wallet-qr-card">
          <h3 className="font-heading font-bold text-lg mb-3">Scan & Pay</h3>
          {wallet.company_qr_url ? (
            <div className="flex flex-col items-center gap-3">
              <img
                src={`${backend}${wallet.company_qr_url}`}
                alt="Company UPI QR"
                className="w-64 h-64 object-contain rounded-lg border border-border bg-white p-3"
                data-testid="wallet-qr-image"
              />
              {wallet.company_upi_id && (
                <div className="text-center">
                  <div className="text-xs text-muted-foreground">UPI ID</div>
                  <div className="font-mono font-semibold">{wallet.company_upi_id}</div>
                </div>
              )}
              <div className="text-xs text-muted-foreground text-center">Payable to <strong>{wallet.company_name}</strong></div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-16 border-2 border-dashed border-border rounded-lg">
              Admin hasn't uploaded a UPI QR yet.<br />Please contact the admin.
            </div>
          )}
        </div>

        <form onSubmit={submit} className="card p-6 space-y-4" data-testid="wallet-submit-form">
          <h3 className="font-heading font-bold text-lg">Submit Payment Proof</h3>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Amount (₹) *</label>
            <input required type="number" step="0.01" min="1" className="input mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wallet-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">UTR / Reference No.</label>
            <input className="input mt-1" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 402512345678" data-testid="wallet-utr" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Note</label>
            <input className="input mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional description" data-testid="wallet-note" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Screenshot (optional)</label>
            <label className="dropzone block mt-1 cursor-pointer" data-testid="wallet-screenshot-drop">
              <UploadCloud className="mx-auto mb-1 text-primary" size={20} />
              <div className="text-sm">{screenshot ? <span className="text-emerald-600">✓ {screenshot.name}</span> : "Upload payment screenshot"}</div>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setScreenshot(e.target.files[0])} />
            </label>
          </div>
          {msg && <div className={`text-sm p-2 rounded-md ${msg.includes("verification") ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300" : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300"}`}>{msg}</div>}
          <button type="submit" disabled={submitting} className="btn btn-primary w-full" data-testid="wallet-submit-btn">
            {submitting ? "Submitting…" : "Submit for Verification"}
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        <div className="p-5 border-b border-border">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ReceiptText size={18} /> Payment History</h3>
        </div>
        <table className="table-clean">
          <thead><tr><th>Receipt No.</th><th>Date</th><th>Amount</th><th>UTR</th><th>Status</th><th>Note</th><th className="text-right">Receipt</th></tr></thead>
          <tbody data-testid="wallet-history">
            {wallet.payments.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No payments yet</td></tr>
            ) : wallet.payments.map((p) => (
              <tr key={p.id}>
                <td className="font-mono font-semibold">{p.payment_number}</td>
                <td className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td className="tabular font-semibold">₹{p.amount.toFixed(2)}</td>
                <td className="font-mono text-xs">{p.utr || "—"}</td>
                <td><span className={`badge ${statusColor(p.status)}`}>{p.status}</span></td>
                <td className="text-xs text-muted-foreground max-w-[200px] truncate">{p.admin_note || p.note || "—"}</td>
                <td className="text-right">
                  <button className="btn btn-ghost !p-2" onClick={() => downloadReceipt(p)} title="Download receipt" data-testid={`receipt-${p.payment_number}`}><Download size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
